import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { catchError, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<
        Notification[]
    >(1);

    constructor(private _httpClient: HttpClient) {}

    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    /**
     * Get all notifications from the real notifications API
     */
    getAll(): Observable<Notification[]> {
        return this._httpClient
            .get<any>('api/notifications')
            .pipe(
                map(response => {
                    const notifications = response.notifications || response;
                    return Array.isArray(notifications) ? notifications : [];
                }),
                tap((notifications) => {
                    this._notifications.next(notifications);
                }),
                catchError(() => {
                    this._notifications.next([]);
                    return of([]);
                })
            );
    }

    create(notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .post<Notification>('api/common/notifications', {
                        notification,
                    })
                    .pipe(
                        map((newNotification) => {
                            this._notifications.next([
                                ...notifications,
                                newNotification,
                            ]);
                            return newNotification;
                        })
                    )
            )
        );
    }

    update(id: string, notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .post<any>(`api/notifications/${id}/read`, {})
                    .pipe(
                        map(() => {
                            const index = notifications.findIndex(
                                (item) => item.id === id
                            );
                            if (index > -1) {
                                notifications[index] = { ...notification, read: true };
                            }
                            this._notifications.next(notifications);
                            return notification;
                        })
                    )
            )
        );
    }

    delete(id: string): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .delete<any>(`api/notifications/${id}`)
                    .pipe(
                        map(() => {
                            const index = notifications.findIndex(
                                (item) => item.id === id
                            );
                            if (index > -1) {
                                notifications.splice(index, 1);
                            }
                            this._notifications.next(notifications);
                            return true;
                        })
                    )
            )
        );
    }

    markAllAsRead(): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .post<any>('api/notifications/read-all', {})
                    .pipe(
                        map(() => {
                            notifications.forEach((notification, index) => {
                                notifications[index].read = true;
                            });
                            this._notifications.next(notifications);
                            return true;
                        })
                    )
            )
        );
    }
}
